import { getLuxPlusSubscriptionItems } from 'checkout/selectors/view/luxPlusSubscription'
import { createSelector } from 'reselect'

export const getLuxPlusSubscriptionRenewalDate = createSelector(
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item?.expiryDate,
  (expiryDate): string | undefined => {
    return expiryDate
  },
)

export const getLuxPlusSubscriptionRenewalOrExpiryDate = createSelector(
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item,
  (state: App.State) => getLuxPlusSubscriptionRenewalDate(state),
  (subscriptionItem, renewalDate): string | undefined => {
    const expiryDate = subscriptionItem?.expiryDate
    const hasActiveLuxPlusSubscriptionRenewal = subscriptionItem?.renewalStatus === 'ALLOW_RENEWAL'
    if (!renewalDate && !expiryDate) return undefined
    if (hasActiveLuxPlusSubscriptionRenewal) return renewalDate
    return expiryDate
  },
)

const isMembershipSubscriptionItemSubscribedTier = (item: App.MemberSubscriptionItem): item is App.MemberSubscriptionItemSubscribedTier => {
  return item.tier !== 'free_preview'
}

export const prioritisedSubscriptionItem = createSelector(
  (state: App.State) => state.auth.account.luxPlus.member.subscription,
  (state: App.State) => getLuxPlusSubscriptionItems(state),
  (memberSubscription, checkoutSubscriptionItems): App.MemberSubscriptionItemSubscribedTier | App.Checkout.LuxPlusSubscriptionItem | undefined => {
    // We always want to prioritise the member subscription item if it exists
    // otherwise we use the first item in the cart isLuxPlusSubscriptionItem that comes from the subscription offer
    // NOTE - if free preview user we want to prioritise the checkoutSubscriptionItems
    return memberSubscription.item && isMembershipSubscriptionItemSubscribedTier(memberSubscription.item) ? memberSubscription.item : checkoutSubscriptionItems[0]
  },
)

export const getCurrentLuxPlusMemberTier = (state: App.State): App.MembershipTier | undefined => state.auth.account.luxPlus.member.subscription.item?.tier

export const getBaseTierOfferWithNoJoiningFee = createSelector(
  (state: App.State) => state.luxPlus.products.subscription.offers,
  (loyaltyProductsSubscriptionOffers): App.SubscriptionOffer | undefined => loyaltyProductsSubscriptionOffers?.find(offer => offer.joinFeeWaived),
)

export const getBaseTierOfferWithJoiningFee = createSelector(
  (state: App.State) => state.luxPlus.products.subscription.offers,
  (loyaltyProductsSubscriptionOffers): App.SubscriptionOffer | undefined => loyaltyProductsSubscriptionOffers?.find(offer => !offer.joinFeeWaived),
)

/**
 * @returns A LuxPlus+ tier where inclusions are viewable for the current user i.e.
 *  there is no such thing as a free preview tier inclusion. We default back to the base tier
 */
export function getInclusionsViewableLuxPlusTier(state: App.State): App.MembershipSubscriptionTier {
  const currentLuxPlusTier = getCurrentLuxPlusMemberTier(state) ?? 'base'
  return currentLuxPlusTier === 'free_preview' ? 'base' : currentLuxPlusTier
}

export const getRenewalOffer = createSelector(
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item,
  (state: App.State) => state.luxPlus.products.subscription.offers,
  (state: App.State) => state.luxPlus.products.subscription.expiredOffers,
  (memberSubscriptionItem, offers, expiredOffers): { offer: App.SubscriptionOffer | undefined; isRenewal: boolean } => {
    if (
      !memberSubscriptionItem?.isWithinRenewalGracePeriod ||
      memberSubscriptionItem?.currentSubscriptionPrice === undefined
    ) {
      return { offer: undefined, isRenewal: false }
    }

    const fromActiveOffers = offers?.find(offer =>
      offer.price === memberSubscriptionItem.currentSubscriptionPrice &&
      offer.joinFeeWaived &&
      offer.region === memberSubscriptionItem.region,
    )
    if (fromActiveOffers) {
      return { offer: fromActiveOffers, isRenewal: true }
    }

    const fromExpiredOffers = expiredOffers?.find(offer =>
      offer.price === memberSubscriptionItem.currentSubscriptionPrice &&
      offer.joinFeeWaived &&
      offer.region === memberSubscriptionItem.region,
    )
    if (fromExpiredOffers) {
      return { offer: fromExpiredOffers, isRenewal: true }
    }

    return { offer: undefined, isRenewal: false }
  },
)
